import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { formAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { 
  ArrowLeft, Plus, Trash2, GripVertical, Save, X,
  Type, Mail, Phone, AlignLeft, ChevronDown, CheckSquare,
  Circle, Calendar, Hash, Code, Sparkles, Lock
} from 'lucide-react';
import { toast } from 'sonner';

// Hardcoded V1 preview of the Pavé Uni preset for the builder UI.
// Server is the source of truth — this is purely a read-only summary
// shown to the workspace owner before they save the AI form.
const PAVE_UNI_STEP_PREVIEW = [
  { key: 'work_type', label: 'Type de travaux' },
  { key: 'location', label: 'Emplacement principal' },
  { key: 'surface_range', label: 'Surface approximative' },
  { key: 'condition', label: 'État du pavé / affaissement' },
  { key: 'access_difficulty', label: "Difficulté d'accès" },
  { key: 'additional_elements', label: 'Éléments additionnels' },
  { key: 'timeline', label: 'Délai souhaité' },
  { key: 'project_notes', label: 'Précisions du client' },
  { key: 'photos', label: 'Photos (optionnel)' },
  { key: 'contact', label: 'Coordonnées' },
];

// Read-only preview of the Fissures & Drains preset (fondation_drain).
// Several steps are conditional (fissure vs drain path) — noted in the UI.
const FONDATION_DRAIN_STEP_PREVIEW = [
  { key: 'main_need', label: 'Besoin principal (fissure ou drain)' },
  { key: 'crack_location', label: 'Emplacement de la fissure *' },
  { key: 'water_infiltration', label: "Infiltration d'eau active *" },
  { key: 'crack_count', label: 'Nombre de fissures *' },
  { key: 'crack_excavator_access', label: 'Accès mini-excavatrice (fissure) *' },
  { key: 'excavation_method', label: "Méthode d'excavation *" },
  { key: 'drain_service', label: 'Service drain français *' },
  { key: 'drain_symptoms', label: 'Symptômes observés *' },
  { key: 'drain_age', label: 'Âge du drain *' },
  { key: 'drain_obstacles', label: 'Obstacles au-dessus du drain *' },
  { key: 'camera_access', label: 'Accès caméra existant *' },
  { key: 'camera_excavation_method', label: "Création de l'accès caméra *" },
  { key: 'section_location', label: 'Emplacement de la section *' },
  { key: 'section_length_ft', label: 'Longueur de la section (pi) *' },
  { key: 'section_depth_ft', label: 'Profondeur (pi) *' },
  { key: 'section_excavator_access', label: 'Accès mini-excavatrice (section) *' },
  { key: 'approx_linear_feet', label: 'Longueur du drain (pi lin.) *' },
  { key: 'project_notes', label: 'Précisions du client' },
  { key: 'photos', label: 'Photos (optionnel)' },
  { key: 'contact', label: 'Coordonnées' },
];

const AI_PRESET_OPTIONS = [
  { id: 'pave_uni', labelFr: 'Pavé Uni', labelEn: 'Interlocking pavers' },
  { id: 'fondation_drain', labelFr: 'Fissures et drains français', labelEn: 'Foundation cracks & French drains' },
];

const AI_PRESET_PREVIEWS = {
  pave_uni: PAVE_UNI_STEP_PREVIEW,
  fondation_drain: FONDATION_DRAIN_STEP_PREVIEW,
};

const FIELD_TYPE_ICONS = {
  text: Type,
  email: Mail,
  phone: Phone,
  textarea: AlignLeft,
  select: ChevronDown,
  checkbox: CheckSquare,
  radio: Circle,
  date: Calendar,
  number: Hash,
};

export function FormBuilderPage() {
  const { formId } = useParams();
  const navigate = useNavigate();
  const { workspace } = useAuth();
  const { t, language } = useLanguage();
  const isEditing = !!formId;
  
  const [isLoading, setIsLoading] = useState(isEditing);
  const [isSaving, setIsSaving] = useState(false);
  
  // Form data
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [fields, setFields] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [notificationEmails, setNotificationEmails] = useState('');
  const [honeypotEnabled, setHoneypotEnabled] = useState(true);
  const [formSlug, setFormSlug] = useState('');
  const [showEmbed, setShowEmbed] = useState(false);

  // Form type — V1 supports "standard" (default, drag-and-drop fields)
  // and "ai_estimation" (preset-driven wizard, e.g. "pave_uni").
  // Switching to ai_estimation IGNORES the user's free-form `fields`.
  const [formType, setFormType] = useState('standard');
  const [aiPresetId, setAiPresetId] = useState('pave_uni');
  const [emergencyPhone, setEmergencyPhone] = useState('');
  const [customSlug, setCustomSlug] = useState('');

  // Theme settings (optional — null => keep current default dark styling)
  // Light contractor preset = sensible defaults shown in the UI when the user
  // first enables theming for a form. Saved as-is on the form document.
  const [themeEnabled, setThemeEnabled] = useState(false);
  const [themeMode, setThemeMode] = useState('light');
  const [bgColor, setBgColor] = useState('#F5F5F4');
  const [cardBgColor, setCardBgColor] = useState('#FFFFFF');
  const [primaryColor, setPrimaryColor] = useState('#F59E0B');
  const [buttonTextColor, setButtonTextColor] = useState('#FFFFFF');
  const [textColor, setTextColor] = useState('#0F172A');
  const [inputBgColor, setInputBgColor] = useState('#FFFFFF');
  const [inputBorderColor, setInputBorderColor] = useState('#D6D3D1');
  
  // Field editor
  const [editingField, setEditingField] = useState(null);

  // Field types with translated labels
  const FIELD_TYPES = [
    { value: 'text', label: t('fieldTypes.text'), icon: Type },
    { value: 'email', label: t('fieldTypes.email'), icon: Mail },
    { value: 'phone', label: t('fieldTypes.phone'), icon: Phone },
    { value: 'textarea', label: t('fieldTypes.textarea'), icon: AlignLeft },
    { value: 'select', label: t('fieldTypes.select'), icon: ChevronDown },
    { value: 'checkbox', label: t('fieldTypes.checkbox'), icon: CheckSquare },
    { value: 'radio', label: t('fieldTypes.radio'), icon: Circle },
    { value: 'date', label: t('fieldTypes.date'), icon: Calendar },
    { value: 'number', label: t('fieldTypes.number'), icon: Hash },
  ];

  // Default fields with translated labels
  const getDefaultFields = () => [
    { 
      field_id: 'f1', 
      label: t('defaultFields.fullName'), 
      type: 'text', 
      required: true, 
      placeholder: t('defaultFields.fullNamePlaceholder'), 
      order: 0 
    },
    { 
      field_id: 'f2', 
      label: t('defaultFields.phone'), 
      type: 'phone', 
      required: true, 
      placeholder: t('defaultFields.phonePlaceholder'), 
      order: 1 
    },
    { 
      field_id: 'f3', 
      label: t('defaultFields.email'), 
      type: 'email', 
      required: false, 
      placeholder: t('defaultFields.emailPlaceholder'), 
      order: 2 
    },
    { 
      field_id: 'f4', 
      label: t('defaultFields.city'), 
      type: 'text', 
      required: true, 
      placeholder: t('defaultFields.cityPlaceholder'), 
      order: 3 
    },
    { 
      field_id: 'f5', 
      label: t('defaultFields.serviceRequired'), 
      type: 'select', 
      required: true, 
      options: workspace?.services || [], 
      order: 4 
    },
    { 
      field_id: 'f6', 
      label: t('defaultFields.message'), 
      type: 'textarea', 
      required: false, 
      placeholder: t('defaultFields.messagePlaceholder'), 
      order: 5 
    },
  ];

  useEffect(() => {
    if (isEditing) {
      loadForm();
    } else {
      // Initialize with default fields
      const initialFields = getDefaultFields().map(f => ({
        ...f,
        field_id: `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      }));
      setFields(initialFields);
      setSuccessMessage(t('forms.defaultConfirmation'));
    }
  }, [formId, language]);

  const loadForm = async () => {
    try {
      const response = await formAPI.get(formId);
      const form = response.data;
      setName(form.name);
      setDescription(form.description || '');
      setFields(form.fields || []);
      setSuccessMessage(form.success_message || t('forms.defaultConfirmation'));
      setNotificationEmails(form.notification_emails?.join(', ') || '');
      setHoneypotEnabled(form.honeypot_enabled !== false);
      setFormSlug(form.slug || '');
      // AI Estimation hydration — preserves choice on re-open.
      if (form.form_type === 'ai_estimation') {
        setFormType('ai_estimation');
        setAiPresetId(form.ai_estimation_preset || 'pave_uni');
        setEmergencyPhone(form.emergency_phone || '');
      } else {
        setFormType('standard');
      }
      // Hydrate theme if previously saved on this form. Absence => default styling.
      if (form.theme && Object.values(form.theme).some(v => v !== null && v !== undefined && v !== '')) {
        setThemeEnabled(true);
        if (form.theme.mode) setThemeMode(form.theme.mode);
        if (form.theme.background_color) setBgColor(form.theme.background_color);
        if (form.theme.card_background_color) setCardBgColor(form.theme.card_background_color);
        if (form.theme.primary_color) setPrimaryColor(form.theme.primary_color);
        if (form.theme.button_text_color) setButtonTextColor(form.theme.button_text_color);
        if (form.theme.text_color) setTextColor(form.theme.text_color);
        if (form.theme.input_background_color) setInputBgColor(form.theme.input_background_color);
        if (form.theme.input_border_color) setInputBorderColor(form.theme.input_border_color);
      }
    } catch (err) {
      console.error('Error loading form:', err);
      toast.error(t('forms.formNotFound'));
      navigate('/forms');
    } finally {
      setIsLoading(false);
    }
  };

  const addField = (type) => {
    const fieldType = FIELD_TYPES.find(ft => ft.value === type);
    const newField = {
      field_id: `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      label: fieldType?.label || t('defaultFields.newField'),
      type,
      placeholder: '',
      required: false,
      options: type === 'select' || type === 'radio' ? ['', '', '', '', ''] : [],
      order: fields.length,
    };
    setFields([...fields, newField]);
    setEditingField(newField.field_id);
  };

  const updateField = (fieldId, updates) => {
    setFields(prev => prev.map(f => 
      f.field_id === fieldId ? { ...f, ...updates } : f
    ));
  };

  const removeField = (fieldId) => {
    setFields(prev => prev.filter(f => f.field_id !== fieldId));
    if (editingField === fieldId) setEditingField(null);
  };

  const moveField = (index, direction) => {
    const newFields = [...fields];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newFields.length) return;
    
    [newFields[index], newFields[newIndex]] = [newFields[newIndex], newFields[index]];
    newFields.forEach((f, i) => f.order = i);
    setFields(newFields);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error(t('forms.formNameRequired'));
      return;
    }
    
    // For standard forms, require at least one field. AI estimation forms
    // are preset-driven and don't use the `fields` array.
    if (formType === 'standard' && fields.length === 0) {
      toast.error(t('forms.addAtLeastOneField'));
      return;
    }

    setIsSaving(true);
    
    try {
      const formData = {
        name: name.trim(),
        description: description.trim(),
        fields: formType === 'standard'
          ? fields.map((f, i) => ({ ...f, order: i }))
          : [],
        success_message: successMessage,
        notification_emails: notificationEmails.split(',').map(e => e.trim()).filter(Boolean),
        form_type: formType,
        ai_estimation_preset: formType === 'ai_estimation' ? aiPresetId : null,
        emergency_phone: formType === 'ai_estimation' ? emergencyPhone.trim() : '',
        // Send theme only when enabled — otherwise null clears it (= default styling).
        theme: themeEnabled ? {
          mode: themeMode,
          background_color: bgColor,
          card_background_color: cardBgColor,
          primary_color: primaryColor,
          button_text_color: buttonTextColor,
          text_color: textColor,
          input_background_color: inputBgColor,
          input_border_color: inputBorderColor,
        } : null,
      };

      if (isEditing) {
        await formAPI.update(formId, formData);
        toast.success(t('forms.formUpdated'));
      } else {
        const response = await formAPI.create({ ...formData, slug: customSlug.trim() || undefined });
        toast.success(t('forms.formCreated'));
        navigate(`/forms/${response.data.form_id}`);
      }
    } catch (err) {
      console.error('Error saving form:', err);
      const message = err.response?.data?.detail || t('forms.errorSaving');
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="form-builder-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/forms')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="font-heading text-3xl font-black tracking-tight">
              {isEditing ? t('forms.editForm') : t('forms.newFormTitle')}
            </h1>
          </div>
        </div>
        <div className="flex gap-3">
          {isEditing && formSlug && (
            <Button variant="outline" onClick={() => setShowEmbed(true)} data-testid="embed-code-btn">
              <Code className="mr-2 h-4 w-4" />
              {language === 'fr' ? "Code d'integration" : 'Embed code'}
            </Button>
          )}
          <Button variant="outline" onClick={() => navigate('/forms')}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={isSaving} data-testid="save-form-btn">
            <Save className="mr-2 h-4 w-4" />
            {isSaving ? t('forms.saving') : t('forms.saveForm')}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form Settings */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('forms.settings')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* ============ Form type selector (Standard vs AI) ============ */}
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Type de formulaire' : 'Form type'}</Label>
                <div className="grid grid-cols-1 gap-2">
                  <button
                    type="button"
                    onClick={() => setFormType('standard')}
                    className={`text-left p-3 rounded-lg border-2 transition-all ${
                      formType === 'standard'
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/40'
                    }`}
                    data-testid="form-type-standard-btn"
                  >
                    <div className="flex items-start gap-3">
                      <Type className={`h-5 w-5 mt-0.5 ${formType === 'standard' ? 'text-primary' : 'text-muted-foreground'}`} />
                      <div className="flex-1">
                        <div className="font-semibold text-sm">
                          {language === 'fr' ? 'Standard' : 'Standard'}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {language === 'fr'
                            ? 'Formulaire classique avec champs personnalisables (par défaut).'
                            : 'Classic form with customizable fields (default).'}
                        </div>
                      </div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setFormType('ai_estimation')}
                    className={`text-left p-3 rounded-lg border-2 transition-all ${
                      formType === 'ai_estimation'
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/40'
                    }`}
                    data-testid="form-type-ai-btn"
                  >
                    <div className="flex items-start gap-3">
                      <Sparkles className={`h-5 w-5 mt-0.5 ${formType === 'ai_estimation' ? 'text-primary' : 'text-muted-foreground'}`} />
                      <div className="flex-1">
                        <div className="font-semibold text-sm flex items-center gap-2">
                          {language === 'fr' ? 'IA avec estimation' : 'AI with estimation'}
                          <Badge variant="secondary" className="text-[10px] py-0">V1</Badge>
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {language === 'fr'
                            ? 'Wizard étape par étape, optimisé pour la conversion. Choisissez le préset ci-dessous.'
                            : 'Step-by-step wizard optimized for conversion. Pick the preset below.'}
                        </div>
                      </div>
                    </div>
                  </button>
                </div>

                {formType === 'ai_estimation' && (
                  <div className="space-y-1.5 pt-2" data-testid="ai-preset-selector">
                    <Label>{language === 'fr' ? 'Préset du wizard IA' : 'AI wizard preset'}</Label>
                    <div className="grid grid-cols-1 gap-2">
                      {AI_PRESET_OPTIONS.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => setAiPresetId(p.id)}
                          className={`text-left px-3 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${
                            aiPresetId === p.id
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:border-primary/40'
                          }`}
                          data-testid={`ai-preset-option-${p.id}`}
                        >
                          {language === 'fr' ? p.labelFr : p.labelEn}
                        </button>
                      ))}
                    </div>
                    <div className="space-y-1.5 pt-3">
                      <Label htmlFor="emergency-phone">
                        {language === 'fr' ? "Téléphone d'urgence (bouton « Appeler »)" : 'Emergency phone ("Call" button)'}
                      </Label>
                      <Input
                        id="emergency-phone"
                        type="tel"
                        value={emergencyPhone}
                        onChange={(e) => setEmergencyPhone(e.target.value)}
                        placeholder="418 555-0123"
                        data-testid="ai-emergency-phone-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        {language === 'fr'
                          ? "Affiché au visiteur en cas d'infiltration d'eau active. Si vide, le téléphone de l'entreprise (Image de marque) est utilisé."
                          : 'Shown to visitors when active water infiltration is reported. Falls back to the company phone (Branding) when empty.'}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <Separator />

              <div className="space-y-2">
                <Label htmlFor="form-name">{t('forms.formName')} *</Label>
                <Input
                  id="form-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('forms.formNamePlaceholder')}
                  data-testid="form-name-input"
                />
              </div>

              {!isEditing && (
                <div className="space-y-2">
                  <Label htmlFor="form-slug">
                    {language === 'fr' ? 'Adresse publique (slug) — optionnel' : 'Public address (slug) — optional'}
                  </Label>
                  <Input
                    id="form-slug"
                    value={customSlug}
                    onChange={(e) => setCustomSlug(e.target.value.toLowerCase())}
                    placeholder={language === 'fr' ? 'ex. estimation-fissures-drains-francais' : 'e.g. estimation-fissures-drains-francais'}
                    data-testid="form-slug-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    {language === 'fr'
                      ? "Laissez vide pour générer l'adresse à partir du nom. Non modifiable après la création (les iframes en dépendent)."
                      : 'Leave empty to generate it from the name. Cannot be changed after creation (iframes depend on it).'}
                  </p>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="form-desc">{t('forms.formDescription')}</Label>
                <Textarea
                  id="form-desc"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('forms.formDescPlaceholder')}
                  rows={2}
                />
              </div>
              
              <Separator />
              
              <div className="space-y-2">
                <Label htmlFor="success-msg">{t('forms.confirmationMessage')}</Label>
                <Textarea
                  id="success-msg"
                  value={successMessage}
                  onChange={(e) => setSuccessMessage(e.target.value)}
                  placeholder={t('forms.confirmationPlaceholder')}
                  rows={2}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="notif-emails">{t('forms.notificationEmails')}</Label>
                <Input
                  id="notif-emails"
                  value={notificationEmails}
                  onChange={(e) => setNotificationEmails(e.target.value)}
                  placeholder={t('forms.notificationPlaceholder')}
                />
                <p className="text-xs text-muted-foreground">
                  {t('forms.separateEmails')}
                </p>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>{t('forms.antiSpam')}</Label>
                  <p className="text-xs text-muted-foreground">{t('forms.honeypotEnabled')}</p>
                </div>
                <Switch
                  checked={honeypotEnabled}
                  onCheckedChange={setHoneypotEnabled}
                />
              </div>
            </CardContent>
          </Card>

          {/* Theme / Apparence — optional public form skinning */}
          <Card data-testid="form-theme-card">
            <CardHeader>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <CardTitle>{language === 'fr' ? 'Apparence du formulaire public' : 'Public form appearance'}</CardTitle>
                  <CardDescription className="mt-1">
                    {language === 'fr'
                      ? 'Personnalisez les couleurs du formulaire affiché sur votre site. Si désactivé, le style par défaut SpaceLeadX est utilisé.'
                      : 'Customize the colors of the form displayed on your site. If disabled, the default SpaceLeadX styling is used.'}
                  </CardDescription>
                </div>
                <Switch
                  checked={themeEnabled}
                  onCheckedChange={setThemeEnabled}
                  data-testid="form-theme-enabled-switch"
                />
              </div>
            </CardHeader>
            {themeEnabled && (
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Mode' : 'Mode'}</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={themeMode === 'light' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setThemeMode('light')}
                      data-testid="form-theme-mode-light"
                    >
                      {language === 'fr' ? 'Clair' : 'Light'}
                    </Button>
                    <Button
                      type="button"
                      variant={themeMode === 'dark' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setThemeMode('dark')}
                      data-testid="form-theme-mode-dark"
                    >
                      {language === 'fr' ? 'Sombre' : 'Dark'}
                    </Button>
                  </div>
                </div>

                {[
                  { id: 'bg', label: language === 'fr' ? 'Fond de page' : 'Page background', val: bgColor, set: setBgColor },
                  { id: 'card', label: language === 'fr' ? 'Fond de la carte' : 'Card background', val: cardBgColor, set: setCardBgColor },
                  { id: 'primary', label: language === 'fr' ? 'Couleur principale (boutons)' : 'Primary color (buttons)', val: primaryColor, set: setPrimaryColor },
                  { id: 'btn-text', label: language === 'fr' ? 'Texte du bouton' : 'Button text', val: buttonTextColor, set: setButtonTextColor },
                  { id: 'text', label: language === 'fr' ? 'Couleur du texte' : 'Text color', val: textColor, set: setTextColor },
                  { id: 'input-bg', label: language === 'fr' ? 'Fond des champs' : 'Input background', val: inputBgColor, set: setInputBgColor },
                  { id: 'input-border', label: language === 'fr' ? 'Bordure des champs' : 'Input border', val: inputBorderColor, set: setInputBorderColor },
                ].map(c => (
                  <div key={c.id} className="space-y-2">
                    <Label htmlFor={`theme-${c.id}`}>{c.label}</Label>
                    <div className="flex items-center gap-3">
                      <input
                        id={`theme-${c.id}`}
                        type="color"
                        value={c.val}
                        onChange={(e) => c.set(e.target.value)}
                        className="h-10 w-14 rounded-md border border-input bg-transparent cursor-pointer"
                        data-testid={`form-theme-${c.id}-input`}
                      />
                      <Input
                        value={c.val}
                        onChange={(e) => c.set(e.target.value)}
                        placeholder="#000000"
                        className="font-mono text-sm"
                      />
                    </div>
                  </div>
                ))}

                <div className="rounded-md border p-3 text-xs text-muted-foreground bg-muted/30">
                  {language === 'fr'
                    ? "Astuce : les changements s'appliquent à la page publique /embed/<slug> et /soumission/<slug> dès la sauvegarde."
                    : 'Tip: changes apply to the public /embed/<slug> and /soumission/<slug> page as soon as you save.'}
                </div>
              </CardContent>
            )}
          </Card>

          {/* Add Field — STANDARD forms only. AI estimation forms use a
              preset-driven wizard (see right panel preview). */}
          {formType === 'standard' && (
          <Card>
            <CardHeader>
              <CardTitle>{t('forms.addField')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-2">
                {FIELD_TYPES.map((type) => {
                  const Icon = type.icon;
                  return (
                    <Button
                      key={type.value}
                      variant="outline"
                      className="h-auto py-3 flex-col gap-1"
                      onClick={() => addField(type.value)}
                    >
                      <Icon className="h-4 w-4" />
                      <span className="text-xs">{type.label}</span>
                    </Button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
          )}
        </div>

        {/* Fields List (Standard) — or Preset preview (AI) */}
        <div className="lg:col-span-2 space-y-4">
          {formType === 'ai_estimation' ? (
            <Card data-testid="ai-preset-preview-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  {language === 'fr' ? 'Aperçu du wizard IA' : 'AI Wizard preview'}
                  <Badge variant="secondary" className="ml-2" data-testid="ai-preset-badge">
                    {(() => {
                      const p = AI_PRESET_OPTIONS.find((o) => o.id === aiPresetId);
                      const label = p ? (language === 'fr' ? p.labelFr : p.labelEn) : aiPresetId;
                      return language === 'fr' ? `Préset : ${label}` : `Preset: ${label}`;
                    })()}
                  </Badge>
                </CardTitle>
                <CardDescription>
                  {language === 'fr'
                    ? "Wizard optimisé pour la qualification du projet. Les étapes et options sont gérées côté serveur (non modifiables). Certaines étapes marquées * sont conditionnelles selon les réponses du visiteur."
                    : 'Wizard optimized to qualify the project. Steps & options are server-managed (read-only). Steps marked * are conditional based on visitor answers.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="rounded-md border p-3 text-xs bg-muted/40 flex items-start gap-2">
                  <Lock className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <span className="text-muted-foreground">
                    {language === 'fr'
                      ? "Le contenu du wizard est verrouillé en V1. Les réponses des visiteurs seront stockées avec des clés stables, prêtes pour le calculateur de prix, le score de lead et l'analyse photo IA."
                      : 'Wizard content is locked in V1. Visitor answers are stored under stable keys, ready for the price calculator, lead scoring and AI photo analysis.'}
                  </span>
                </div>
                <ol className="space-y-2">
                  {(AI_PRESET_PREVIEWS[aiPresetId] || PAVE_UNI_STEP_PREVIEW).map((s, i) => (
                    <li
                      key={s.key}
                      className="flex items-center gap-3 px-3 py-2 rounded-md border bg-background"
                      data-testid={`ai-preset-step-${s.key}`}
                    >
                      <span className="flex items-center justify-center h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-bold">
                        {i + 1}
                      </span>
                      <span className="text-sm font-medium">{s.label}</span>
                      <span className="ml-auto text-[10px] font-mono text-muted-foreground">{s.key}</span>
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>
          ) : (
          <Card>
            <CardHeader>
              <CardTitle>{t('forms.formFields')} ({fields.length})</CardTitle>
              <CardDescription>
                {t('forms.formFieldsDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {fields.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
                  <p>{t('forms.noFields')}</p>
                  <p className="text-sm">{t('forms.addFieldsPrompt')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {fields.map((field, index) => {
                    const FieldIcon = FIELD_TYPE_ICONS[field.type] || Type;
                    const isEditingThis = editingField === field.field_id;
                    
                    return (
                      <div
                        key={field.field_id}
                        className={`border rounded-lg transition-all ${
                          isEditingThis ? 'border-primary shadow-md' : 'border-border hover:border-primary/50'
                        }`}
                      >
                        {/* Field Header */}
                        <div
                          className="flex items-center gap-3 p-3 cursor-pointer"
                          onClick={() => setEditingField(isEditingThis ? null : field.field_id)}
                        >
                          <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab" />
                          <FieldIcon className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium flex-1">{field.label}</span>
                          {field.required && (
                            <Badge variant="secondary" className="text-xs">{t('common.required')}</Badge>
                          )}
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={(e) => { e.stopPropagation(); moveField(index, -1); }}
                              disabled={index === 0}
                            >
                              ↑
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={(e) => { e.stopPropagation(); moveField(index, 1); }}
                              disabled={index === fields.length - 1}
                            >
                              ↓
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive"
                              onClick={(e) => { e.stopPropagation(); removeField(field.field_id); }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        {/* Field Editor (expanded) */}
                        {isEditingThis && (
                          <div className="p-4 pt-0 space-y-4 border-t">
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label>{t('forms.fieldLabel')}</Label>
                                <Input
                                  value={field.label}
                                  onChange={(e) => updateField(field.field_id, { label: e.target.value })}
                                  placeholder={t('forms.fieldLabel')}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>{t('forms.fieldPlaceholder')}</Label>
                                <Input
                                  value={field.placeholder || ''}
                                  onChange={(e) => updateField(field.field_id, { placeholder: e.target.value })}
                                  placeholder={t('forms.placeholderText')}
                                />
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-4">
                              <div className="flex items-center gap-2">
                                <Switch
                                  checked={field.required}
                                  onCheckedChange={(checked) => updateField(field.field_id, { required: checked })}
                                />
                                <Label>{t('forms.fieldRequired')}</Label>
                              </div>
                            </div>
                            
                            {/* Options for select/radio */}
                            {(field.type === 'select' || field.type === 'radio') && (
                              <div className="space-y-2">
                                <Label>{t('forms.fieldOptions')}</Label>
                                <div className="space-y-2">
                                  {(field.options || []).map((opt, idx) => (
                                    <div key={idx} className="flex items-center gap-2">
                                      <span className="text-xs text-muted-foreground w-5 text-right">{idx + 1}.</span>
                                      <Input
                                        value={opt}
                                        onChange={(e) => {
                                          const newOpts = [...(field.options || [])];
                                          newOpts[idx] = e.target.value;
                                          updateField(field.field_id, { options: newOpts });
                                        }}
                                        placeholder={`Option ${idx + 1}`}
                                        className="flex-1"
                                        data-testid={`option-input-${idx}`}
                                      />
                                      {(field.options || []).length > 2 && (
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                          onClick={() => {
                                            const newOpts = (field.options || []).filter((_, i) => i !== idx);
                                            updateField(field.field_id, { options: newOpts });
                                          }}
                                          data-testid={`remove-option-${idx}`}
                                        >
                                          <X className="h-4 w-4" />
                                        </Button>
                                      )}
                                    </div>
                                  ))}
                                </div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => updateField(field.field_id, { options: [...(field.options || []), ''] })}
                                  data-testid="add-option-btn"
                                >
                                  <Plus className="h-3.5 w-3.5 mr-1" />
                                  {language === 'fr' ? 'Ajouter une option' : 'Add an option'}
                                </Button>
                                {field.type === 'select' && workspace?.services?.length > 0 && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => updateField(field.field_id, { options: workspace.services })}
                                  >
                                    {t('forms.useMyServices')}
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
          )}
        </div>
      </div>

      {/* Embed Code Modal */}
      <Dialog open={showEmbed} onOpenChange={setShowEmbed}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Code className="h-5 w-5" />
              {language === 'fr' ? "Code d'integration" : 'Embed code'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr'
                ? 'Copiez ce code et collez-le dans votre site web pour integrer le formulaire.'
                : 'Copy this code and paste it into your website to embed the form.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Code iframe' : 'Iframe code'}</Label>
              <div className="relative">
                <pre className="bg-muted p-4 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap break-all font-mono" data-testid="embed-code-block">
{`<iframe
  src="${window.location.origin}/soumission/${formSlug}"
  width="100%"
  height="700"
  frameborder="0"
  style="border:none; max-width:600px;"
></iframe>`}
                </pre>
                <Button
                  variant="outline"
                  size="sm"
                  className="absolute top-2 right-2"
                  data-testid="copy-embed-btn"
                  onClick={() => {
                    navigator.clipboard.writeText(
`<iframe\n  src="${window.location.origin}/soumission/${formSlug}"\n  width="100%"\n  height="700"\n  frameborder="0"\n  style="border:none; max-width:600px;"\n></iframe>`
                    );
                    toast.success(language === 'fr' ? 'Code copie!' : 'Code copied!');
                  }}
                >
                  {language === 'fr' ? 'Copier' : 'Copy'}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'URL directe du formulaire' : 'Direct form URL'}</Label>
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={`${window.location.origin}/soumission/${formSlug}`}
                  className="text-xs font-mono"
                  data-testid="embed-url"
                />
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="copy-url-btn"
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/soumission/${formSlug}`);
                    toast.success(language === 'fr' ? 'URL copiee!' : 'URL copied!');
                  }}
                >
                  {language === 'fr' ? 'Copier' : 'Copy'}
                </Button>
              </div>
            </div>
            <div className="text-xs text-muted-foreground space-y-1">
              <p><strong>Workspace ID:</strong> {workspace?.workspace_id || 'N/A'}</p>
              <p><strong>Form ID:</strong> {formId || 'N/A'}</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
